import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import { rateLabel } from '@aeris/printing'
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  Field,
  InlineAlert,
  Input,
  RadioGroup,
} from '@aeris/ui'

import {
  fetchCommissionRates,
  periodLabel,
  periodOf,
  setCommissionRate,
  shiftPeriod,
} from '../lib/api/commissions.ts'
import { ApiError } from '../lib/api.ts'
import { formatDate } from '../lib/date-format.ts'

import { CommissionPayeePicker } from './commission-payee-picker.tsx'

/**
 * A % de comissão e quem recebe, de uma pessoa da equipe (F137).
 *
 * Duas coisas separadas, com dois botões, porque são gravadas de jeitos
 * diferentes: a % entra como uma linha nova da história, e quem recebe é um
 * cadastro com versão.
 *
 * A % vale a partir de agora OU desde o começo de um mês (F235). O gerente da
 * Elite pôs 1% depois que as vendedoras já vendiam, e o aviso "vendeu sem %"
 * não sumia por mais que ele gravasse — a % só valia dali para a frente.
 */

/** `"2.5000"` → `2,5` para o campo. */
const toField = (rate: string | null): string =>
  rate === null ? '' : rateLabel(rate).replace('%', '')

const NOW = 'now'

/** `2026-09` → `1º de setembro`. */
const firstOf = (period: string): string =>
  `1º de ${periodLabel(period).replace(/ de \d{4}$/, '')}`

export const CommissionRateDialog = ({
  member,
  initialSince = null,
  onClose,
}: {
  member: { userId: string; displayName: string }
  /** O mês já escolhido: quem vem do aviso de Comissões quer aquele mês. */
  initialSince?: string | null
  onClose: () => void
}): ReactNode => {
  const queryClient = useQueryClient()

  const rates = useQuery({
    queryKey: ['commissions', 'rates'],
    queryFn: ({ signal }) => fetchCommissionRates(signal),
  })
  const entry = rates.data?.sellers.find((row) => row.userId === member.userId)

  const [rate, setRate] = useState<string | null>(null)
  const shown = rate ?? toField(entry?.currentRate ?? null)
  const [since, setSince] = useState<string>(initialSince ?? NOW)
  const thisMonth = periodOf(new Date())
  const months = [
    ...new Set([
      thisMonth,
      shiftPeriod(thisMonth, -1),
      ...(initialSince === null ? [] : [initialSince]),
    ]),
  ].sort((a, b) => b.localeCompare(a))
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState<string | null>(null)

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['commissions'] })
  }

  const report = (cause: unknown, fallback: string): void => {
    setSaved(null)
    setError(cause instanceof ApiError ? cause.message : fallback)
  }

  const saveRate = useMutation({
    mutationFn: async () =>
      await setCommissionRate({
        userId: member.userId,
        rate: shown.trim(),
        since: since === NOW ? null : since,
      }),
    onSuccess: async (result) => {
      setRate(null)
      setError(null)
      setSaved(
        since === NOW
          ? `% de comissão agora é ${rateLabel(result.rate)}.`
          : `${rateLabel(result.rate)} desde ${firstOf(since)}: as vendas desde então já contam com esta %.`
      )
      await refresh()
    },
    onError: (cause: unknown) => {
      report(cause, 'Não foi possível gravar a %.')
    },
  })

  const rateValid = /^\d{1,3}([.,]\d{1,4})?$/.test(shown.trim())
  /*
   * "Não mudou" só vale para "a partir de agora". A Cinthia já tinha 1% e
   * precisava de "1% desde setembro": travar o botão por a % ser a mesma era
   * exatamente o caso que esta tela existe para resolver.
   */
  const unchanged =
    since === NOW && shown.trim() === toField(entry?.currentRate ?? null)

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <DialogContent
        title={`Comissão de ${member.displayName}`}
        description="A % vale a partir de agora, ou desde o começo de um mês ainda não fechado. Mês já fechado não muda."
        className="max-w-lg"
      >
        <div className="flex flex-col gap-5">
          <form
            className="flex flex-col gap-2"
            onSubmit={(event) => {
              event.preventDefault()
              if (!rateValid || unchanged) return
              setSaved(null)
              saveRate.mutate()
            }}
          >
            <div className="flex items-end gap-2">
              <Field
                label="% sobre a venda líquida"
                hint="Ex.: 3 ou 2,5. Zero tira a comissão."
                className="flex-1"
              >
                {(field) => (
                  <Input
                    {...field}
                    inputMode="decimal"
                    value={shown}
                    placeholder="0"
                    onChange={(event) => {
                      setRate(event.target.value)
                    }}
                  />
                )}
              </Field>
              <Button
                type="submit"
                variant="primary"
                disabled={!rateValid || unchanged}
                loading={saveRate.isPending}
              >
                Gravar %
              </Button>
            </div>

            <Field label="Vale">
              {(field) => (
                <RadioGroup
                  id={field.id}
                  name="commission-since"
                  value={since}
                  onValueChange={setSince}
                  options={[
                    { value: NOW, label: 'A partir de agora' },
                    ...months.map((period) => ({
                      value: period,
                      label: `Desde ${firstOf(period)}`,
                      description: `As vendas de ${periodLabel(period)} em diante passam a usar esta %.`,
                    })),
                  ]}
                />
              )}
            </Field>

            {entry === undefined || entry.history.length === 0 ? null : (
              <ul
                aria-label="Histórico da %"
                className="flex flex-col gap-0.5 text-(length:--text-xs) text-(--text-muted)"
              >
                {entry.history.slice(0, 5).map((row, index) => (
                  // Duas % desde o mesmo 1º do mês têm o mesmo `validFrom`.
                  <li key={`${row.createdAt}-${String(index)}`}>
                    {`${rateLabel(row.rate)} desde ${formatDate(row.validFrom)}`}
                    {formatDate(row.createdAt) === formatDate(row.validFrom)
                      ? ''
                      : ` (gravada em ${formatDate(row.createdAt)})`}
                    {row.actorName === null ? '' : ` · ${row.actorName}`}
                  </li>
                ))}
              </ul>
            )}
          </form>

          <CommissionPayeePicker
            userId={member.userId}
            displayName={member.displayName}
            payee={entry?.payee ?? null}
            onSaved={(message) => {
              setError(null)
              setSaved(message)
            }}
            onError={(message) => {
              setSaved(null)
              setError(message)
            }}
          />

          {saved === null ? null : (
            <InlineAlert tone="success">{saved}</InlineAlert>
          )}
          {error === null ? null : (
            <InlineAlert tone="danger">{error}</InlineAlert>
          )}

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Fechar
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  )
}
